import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type SubmitEvent, useEffect, useId, useRef, useState } from 'react'

import type { Currency } from '@/bindings/Currency'
import type { Language } from '@/bindings/Language'
import type { OrderingContext } from '@/bindings/OrderingContext'
import type { OrderingItem } from '@/bindings/OrderingItem'
import type { PlaceOrderRequest } from '@/bindings/PlaceOrderRequest'
import type { PlacedOrder } from '@/bindings/PlacedOrder'
import { Button } from '@/components/button'
import { Field, Input, Textarea } from '@/components/field'
import { FormError } from '@/components/form-error'
import { orderingKeys, placeOrder } from '@/features/ordering/api'
import { type Cart, cartTotalMinor, requestLines, setLineNote, setQuantity } from '@/features/ordering/cart'
import { QuantityControl } from '@/features/ordering/menu-list'
import { whereLabel } from '@/features/ordering/order-rules'
import { ApiError, claim } from '@/lib/api'
import { minorUnits } from '@/lib/currencies'
import { useErrorMessage } from '@/lib/i18n/errors'
import { useLocalizedText } from '@/lib/i18n/localized-text'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import { formatPrice, formatQuantity } from '@/lib/menu-format'
import { fromMinor, lineTotalMinor } from '@/lib/quantity'
import { useDismiss } from '@/lib/use-dismiss'

/** Mirror `romi_domain::{MAX_NOTE_LEN, MAX_LINE_NOTE_LEN, MAX_DELIVERY_ADDRESS_LEN}`. */
export const MAX_NOTE_LEN = 500
export const MAX_LINE_NOTE_LEN = 200
export const MAX_DELIVERY_ADDRESS_LEN = 200

export interface CartLinesProps {
  cart: Cart
  items: ReadonlyMap<string, OrderingItem>
  currency: Currency
  /** The restaurant's main language: the T4.3 fallback for item names. */
  main: Language
  disabled: boolean
  /** A placement refusal one line owns (its `item_id` names it); the others are the banner's. */
  lineError: ApiError | undefined
  update: (next: (cart: Cart) => Cart) => void
  /** Shown instead of the list when the cart is empty. */
  empty: string
}

/** The cart's lines with `− qty +` / Remove and each line's own refusal (G6; the staff page too, G12). */
export function CartLines({ cart, items, currency, main, disabled, lineError, update, empty }: CartLinesProps) {
  const t = useT()
  const language = useLanguage()
  const name = useLocalizedText(main)
  const errorMessage = useErrorMessage()
  const price = (value: string) => formatPrice(value, minorUnits(currency), language)
  if (cart.lines.length === 0) return <p className="text-muted text-sm">{empty}</p>
  return (
    <ul className="divide-border divide-y">
      {cart.lines.map((line) => {
        const item = items.get(line.item_id)
        if (!item) return null
        const label = name(item.name)
        const problem = !item.orderable
          ? t('order.lineUnavailable')
          : lineError && lineError.params['item_id'] === line.item_id
            ? errorMessage(lineError)
            : undefined
        return (
          <li key={line.item_id} className="flex flex-col gap-2 py-3">
            <div className="flex items-start gap-3">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <p className="font-medium">{label}</p>
                <p className="text-muted text-sm">
                  {price(item.price)} × {formatQuantity(line.quantity, language)}
                </p>
              </div>
              <span className="font-medium tabular-nums">{price(fromMinor(lineTotalMinor(item.price, line.quantity, item.step, currency), currency))}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <QuantityControl
                name={label}
                step={item.step}
                quantity={line.quantity}
                disabled={disabled || !item.orderable}
                onChange={(quantity) => update((cart) => setQuantity(cart, line.item_id, quantity))}
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={t('order.remove', { name: label })}
                disabled={disabled}
                onClick={() => update((cart) => setQuantity(cart, line.item_id, '0'))}
              >
                {t('order.removeShort')}
              </Button>
            </div>
            <LineNoteField
              name={label}
              value={line.note ?? ''}
              disabled={disabled}
              onChange={(note) => update((cart) => setLineNote(cart, line.item_id, note))}
            />
            {problem && (
              <p role="alert" className="text-danger text-sm">
                {problem}
              </p>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/** "Add a note" until tapped or filled, then the one-line note for this dish ("ít hành"). */
function LineNoteField({ name, value, disabled, onChange }: { name: string; value: string; disabled: boolean; onChange: (note: string) => void }) {
  const t = useT()
  // Never back to closed: clearing the text mid-edit must not swap the input away under the cursor.
  const [open, setOpen] = useState<'closed' | 'restored' | 'tapped'>(value ? 'restored' : 'closed')
  if (open === 'closed') {
    return (
      <Button type="button" size="sm" variant="ghost" className="self-start" disabled={disabled} onClick={() => setOpen('tapped')}>
        {t('order.lineNoteAdd')}
      </Button>
    )
  }
  return (
    <Input
      value={value}
      maxLength={MAX_LINE_NOTE_LEN}
      placeholder={t('order.lineNotePlaceholder')}
      aria-label={t('order.lineNote', { name })}
      autoFocus={open === 'tapped'}
      autoComplete="off"
      enterKeyHint="done"
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      // Enter in a note is not "Place order": the cart form would submit with the rest unfilled.
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.preventDefault()
      }}
    />
  )
}

export interface CartSheetProps {
  open: boolean
  onClose: () => void
  context: OrderingContext
  items: ReadonlyMap<string, OrderingItem>
  /** `undefined` = take-away (G3's `/order` QR); a code = dine-in at that table. */
  tableCode: string | undefined
  cart: Cart
  update: (next: (cart: Cart) => Cart) => void
  /** T8.3: the idempotency key of this attempt (kept across retries of the same cart). */
  attempt: () => string
  endAttempt: () => void
  anonymous: boolean
  onPlaced: (order: PlacedOrder) => void
}

/**
 * The cart, note, delivery address, PIN, and the Place order button (PRD, G6).
 * Stays mounted (rendering nothing) while closed so the drafts and the
 * attempt survive a look back at the menu. Placement errors land on the line, the PIN, or the banner.
 * Never reached while the branch is not taking orders or, in Login mode, anonymous: the page blocks before the menu.
 */
export function CartSheet({ open, onClose, context, items, tableCode, cart, update, attempt, endAttempt, anonymous, onPlaced }: CartSheetProps) {
  const t = useT()
  const language = useLanguage()
  const queryClient = useQueryClient()
  const titleId = useId()
  const panel = useRef<HTMLFormElement>(null)
  const closeButton = useRef<HTMLButtonElement>(null)
  const [note, setNote] = useState('')
  const [address, setAddress] = useState('')
  const [pin, setPin] = useState('')

  const { restaurant, branch } = context
  const decimals = minorUnits(branch.currency)
  const price = (value: string) => formatPrice(value, decimals, language)
  const takeAway = tableCode === undefined
  const where = whereLabel(t, context.table?.name)
  const showAddress = takeAway && branch.delivery_address_enabled
  const unorderable = cart.lines.some((line) => !items.get(line.item_id)?.orderable)
  const total = fromMinor(cartTotalMinor(cart, items, branch.currency), branch.currency)
  const canPlace = cart.lines.length > 0 && !unorderable

  const place = useMutation({
    mutationFn: () => {
      const body: PlaceOrderRequest = {
        idempotency_key: attempt(),
        lines: requestLines(cart),
        ...(tableCode !== undefined ? { table_code: tableCode } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(showAddress && address.trim() ? { delivery_address: address.trim() } : {}),
        ...(branch.pin_required && pin ? { pin } : {}),
      }
      return placeOrder(restaurant.slug, branch.slug, body)
    },
    onSuccess: (order) => {
      endAttempt()
      onPlaced(order)
    },
    onError: (error) => {
      // A verdict (4xx) ends the attempt; a lost answer (network, 5xx) keeps its key for the retry.
      if (error instanceof ApiError && error.status < 500) endAttempt()
      // Sold-out lines, a closed shop, a dead table: the context tells the page what changed.
      void queryClient.invalidateQueries({ queryKey: orderingKeys.context(restaurant.slug, branch.slug, tableCode) })
    },
  })
  const pending = place.isPending

  // The key names the whole request (T8.3): a new note or address is a new attempt, not a retry.
  function change<T>(set: (value: T) => void) {
    return (value: T) => {
      endAttempt()
      set(value)
    }
  }
  const editNote = change(setNote)
  const editAddress = change(setAddress)

  const close = () => {
    if (!pending) onClose()
  }
  useDismiss(open, panel, close)
  useEffect(() => {
    if (open) closeButton.current?.focus()
  }, [open])
  useEffect(() => {
    document.body.classList.toggle('overflow-hidden', open)
    return () => document.body.classList.remove('overflow-hidden')
  }, [open])

  function submit(event: SubmitEvent) {
    event.preventDefault()
    if (!canPlace) return
    place.mutate()
  }

  const lineError = claim(place.error, 'ITEM_UNAVAILABLE', 'INSUFFICIENT_STOCK', 'INVALID_QUANTITY', 'LINE_NOTE_TOO_LONG')
  const pinError = claim(place.error, 'ORDER_PIN_INVALID')
  const bannerError = lineError || pinError ? undefined : place.error

  // Closed: nothing in the DOM (no hidden copy of the menu names), but the drafts and the attempt stay.
  if (!open) return null
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="bg-bg fixed inset-0 z-40 flex flex-col sm:items-center sm:justify-center sm:bg-black/50 sm:p-4"
    >
      <form
        ref={panel}
        onSubmit={submit}
        className="bg-surface sm:border-border flex max-h-full w-full flex-1 flex-col overflow-hidden sm:max-h-[90dvh] sm:max-w-lg sm:flex-none sm:rounded-2xl sm:border"
      >
        <div className="border-border flex items-center justify-between gap-3 border-b px-4 py-3">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-semibold">
              {t('order.cartTitle')}
            </h2>
            <p className="text-muted truncate text-sm">{where}</p>
          </div>
          <Button ref={closeButton} type="button" variant="ghost" size="sm" onClick={close} disabled={pending}>
            {t('order.close')}
          </Button>
        </div>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-3">
          <CartLines
            cart={cart}
            items={items}
            currency={branch.currency}
            main={restaurant.main_language}
            disabled={pending}
            lineError={lineError}
            update={update}
            empty={t('order.cartEmpty')}
          />

          {cart.lines.length > 0 && (
            <>
              <Field label={t('order.note')} hint={t('order.noteHint', { max: MAX_NOTE_LEN })}>
                <Textarea value={note} maxLength={MAX_NOTE_LEN} rows={2} disabled={pending} onChange={(e) => editNote(e.target.value)} />
              </Field>
              {showAddress && (
                <Field label={t('order.deliveryAddress')} hint={t('order.deliveryAddressHint', { max: MAX_DELIVERY_ADDRESS_LEN })}>
                  <Input value={address} maxLength={MAX_DELIVERY_ADDRESS_LEN} autoComplete="off" disabled={pending} onChange={(e) => editAddress(e.target.value)} />
                </Field>
              )}
              {branch.pin_required && (
                <Field label={t('order.pin')} hint={t('order.pinHint')} error={pinError}>
                  <Input
                    value={pin}
                    autoComplete="off"
                    inputMode="numeric"
                    disabled={pending}
                    onChange={(e) => {
                      if (place.error) place.reset()
                      setPin(e.target.value)
                    }}
                  />
                </Field>
              )}
              {anonymous && branch.ordering_mode === 'open' && (
                <p className="text-muted text-sm">{t('order.expiryHint', { minutes: branch.open_order_expiry_minutes })}</p>
              )}
            </>
          )}
        </div>

        <div className="border-border flex flex-col gap-3 border-t px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex items-center justify-between text-lg">
            <span>{t('order.total')}</span>
            <span className="font-semibold tabular-nums">{price(total)}</span>
          </div>
          <FormError error={bannerError} />
          <Button type="submit" block loading={pending} disabled={!canPlace}>
            {t('order.place')}
          </Button>
        </div>
      </form>
    </div>
  )
}
